"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { getCookie, setCookie } from "cookies-next";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { CookieDisclaimer as CookieDisclaimerBar } from "@tranquility-ai/theme-components";
import "@tranquility-ai/theme-components/cookie-disclaimer.css";
import { performClientLogout } from "@/lib/performClientLogout";
import { useReactiveFeatureFlagValue } from "@/app/contexts";
import { readFlagOverridesFromSession } from "@/lib/feature-flag-overrides";
import { useHelioDemo } from "@/app/useHelioDemo";
import { LD_INIT_TIMEOUT_SECONDS } from "@/lib/launch-darkly-init";

/** Cookie that records the user's cookie-disclaimer choice. */
export const COOKIE_CONSENT_NAME = "tai-cookie-consent";
export const COOKIE_CONSENT_ACCEPTED = "accepted";
export const COOKIE_CONSENT_NECESSARY = "necessary";
export const COOKIE_CONSENT_OPTED_OUT = "opted-out";

/** Dispatched on `window` whenever the consent cookie is written. */
export const COOKIE_CONSENT_CHANGE_EVENT = "tai-cookie-consent-change";

/** LaunchDarkly flag key (dashboard) and camelCase form used by the React SDK. */
export const COOKIE_BANNER_FLAG_KEY = "cookie-banner-enabled";
export const COOKIE_BANNER_FLAG_CAMEL = "cookieBannerEnabled";

const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60; // 1 year

const DISMISSED_VALUES = new Set([
  COOKIE_CONSENT_ACCEPTED,
  COOKIE_CONSENT_NECESSARY,
]);

export function readCookieConsent(): string | null {
  const value = getCookie(COOKIE_CONSENT_NAME);
  return typeof value === "string" ? value : null;
}

/** Non-essential tools (e.g. Intercom) require an explicit Accept-all choice. */
export function allowsNonEssentialCookies(
  consent: string | null = readCookieConsent(),
): boolean {
  return consent === COOKIE_CONSENT_ACCEPTED;
}

function productNameForPath(
  pathname: string | null,
  isHelioDemo: boolean,
): string {
  if (pathname?.startsWith("/immigration-reports")) {
    return "Helio";
  }
  // `/meta-viewer` is shared; only brand Helio when the helio-demo flag is on.
  if (pathname?.startsWith("/meta-viewer") && isHelioDemo) {
    return "Helio";
  }
  return "TimePilot";
}

function persistConsent(value: string) {
  setCookie(COOKIE_CONSENT_NAME, value, {
    maxAge: COOKIE_MAX_AGE_SECONDS,
    path: "/",
    sameSite: "lax",
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(COOKIE_CONSENT_CHANGE_EVENT, { detail: { value } }),
    );
  }
}

function hasCookieBannerOverride(
  overrides: ReturnType<typeof readFlagOverridesFromSession>,
): boolean {
  return (
    COOKIE_BANNER_FLAG_CAMEL in overrides ||
    COOKIE_BANNER_FLAG_KEY in overrides
  );
}

type CookieBannerFlagState = "loading" | "enabled" | "disabled";

function resolveCookieBannerFlagState(
  contextValue: unknown,
  ldFlags: Record<string, unknown>,
  ldReady: boolean,
): CookieBannerFlagState {
  const overrides = readFlagOverridesFromSession();
  if (hasCookieBannerOverride(overrides)) {
    return overrides[COOKIE_BANNER_FLAG_CAMEL] === true ||
      overrides[COOKIE_BANNER_FLAG_KEY] === true
      ? "enabled"
      : "disabled";
  }

  if (contextValue !== undefined) {
    return contextValue === true ? "enabled" : "disabled";
  }

  if (!ldReady) {
    return "loading";
  }

  const raw =
    ldFlags[COOKIE_BANNER_FLAG_CAMEL] ?? ldFlags[COOKIE_BANNER_FLAG_KEY];
  return raw === true ? "enabled" : "disabled";
}

function isCookieBannerFlagEnabled(
  contextValue: unknown,
  ldFlags: Record<string, unknown>,
  ldReady = true,
): boolean {
  return resolveCookieBannerFlagState(contextValue, ldFlags, ldReady) === "enabled";
}

function useLaunchDarklyReady(): boolean {
  const ldClient = useLDClient();
  const [ready, setReady] = useState(() => hasCookieBannerOverride(readFlagOverridesFromSession()));

  useEffect(() => {
    if (ready) return;
    if (!ldClient) return;

    let cancelled = false;
    void ldClient
      .waitForInitialization(LD_INIT_TIMEOUT_SECONDS)
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch(() => {
        // Timeout or network failure: LD falls back to default flag values.
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [ldClient, ready]);

  return ready;
}

/**
 * Subscribe to the persisted cookie-consent choice so non-essential tools can
 * react when the user Accepts / chooses Only Necessary / Opts Out.
 *
 * When the cookie-banner LaunchDarkly flag is off, returns ``true`` so existing
 * Intercom behavior is unchanged. While LaunchDarkly is still loading, returns
 * ``false`` so non-essential tools do not start before the flag is known. When
 * the flag is on, only an explicit Accept (``accepted``) allows non-essential tools.
 */
export function useAllowsNonEssentialCookies(): boolean {
  const ldFlags = useFlags();
  const ldReady = useLaunchDarklyReady();
  const flagFromContext = useReactiveFeatureFlagValue(COOKIE_BANNER_FLAG_CAMEL);
  const bannerState = resolveCookieBannerFlagState(
    flagFromContext,
    ldFlags,
    ldReady,
  );
  const [consent, setConsent] = useState<string | null>(() => readCookieConsent());

  useEffect(() => {
    const sync = () => setConsent(readCookieConsent());
    sync();
    window.addEventListener(COOKIE_CONSENT_CHANGE_EVENT, sync);
    return () => window.removeEventListener(COOKIE_CONSENT_CHANGE_EVENT, sync);
  }, []);

  if (bannerState === "loading") return false;
  if (bannerState === "disabled") return true;
  return allowsNonEssentialCookies(consent);
}

/**
 * Fixed bottom cookie disclaimer for TimePilot and Helio (HelioTrace).
 * Gated by LaunchDarkly flag ``cookie-banner-enabled``.
 * Persists the user's choice in a first-party cookie; Opt Out logs them out.
 * Non-essential tools (Intercom) must call ``allowsNonEssentialCookies`` /
 * ``useAllowsNonEssentialCookies`` so Accept vs Only Necessary differ.
 *
 * Must be mounted under an ``LDProvider`` (and preferably ``FeaturesContext``).
 */
export function CookieDisclaimer() {
  const pathname = usePathname();
  const ldFlags = useFlags();
  const ldReady = useLaunchDarklyReady();
  const flagFromContext = useReactiveFeatureFlagValue(COOKIE_BANNER_FLAG_CAMEL);
  const flagEnabled = isCookieBannerFlagEnabled(
    flagFromContext,
    ldFlags,
    ldReady,
  );
  const isHelioDemo = useHelioDemo();

  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const productName = productNameForPath(pathname, isHelioDemo);

  useEffect(() => {
    if (!flagEnabled) {
      setVisible(false);
      return;
    }
    const existing = getCookie(COOKIE_CONSENT_NAME);
    if (typeof existing !== "string" || !DISMISSED_VALUES.has(existing)) {
      setVisible(true);
    }
  }, [flagEnabled]);

  const acceptAll = () => {
    persistConsent(COOKIE_CONSENT_ACCEPTED);
    setVisible(false);
  };

  const acceptNecessaryOnly = () => {
    persistConsent(COOKIE_CONSENT_NECESSARY);
    setVisible(false);
  };

  const optOut = async () => {
    if (busy) return;
    setBusy(true);
    persistConsent(COOKIE_CONSENT_OPTED_OUT);
    setVisible(false);
    try {
      await performClientLogout();
    } catch {
      // If logout redirect fails, keep the opted-out preference and re-show
      // so the user can try again or choose a different option.
      setBusy(false);
      setVisible(true);
    }
  };

  if (!flagEnabled) return null;

  return (
    <CookieDisclaimerBar
      productName={productName}
      open={visible}
      onAccept={acceptAll}
      onNecessaryOnly={acceptNecessaryOnly}
      onOptOut={optOut}
      optOutBusy={busy}
    />
  );
}

export default CookieDisclaimer;
